import { lazy, Suspense } from "react";
import { helperFace } from "../lib/helperPresentation";
import type { HelperState } from "../lib/types";

// Both libraries (libraries.dev, MIT) draw on a plain 2D canvas and pause off screen and in a
// hidden tab on their own. Loaded on first use, so a chat without helpers never downloads them.
const BotAvatar = lazy(() => import("bot-avatars").then((m) => ({ default: m.BotAvatar })));
const loadOrb = () => import("thinking-orbs");
const ThinkingOrb = lazy(() => loadOrb().then((m) => ({ default: m.ThinkingOrb })));
// The orb is the "Starting / Working" signature: fetch it as soon as the app is idle, so the
// first "Starting" never shows a placeholder dot before the orb (Julian, 30 Sep).
if (typeof window !== "undefined") {
  const warm = () => { void loadOrb().catch(() => {}); };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  if (idle) idle(warm); else window.setTimeout(warm, 1500);
}

/** A working bot hops, a done or waiting one idles (looks round, blinks), a failed or stopped one holds still. */
function botState(state: HelperState): { state: "working" | "default"; paused: boolean } {
  if (state === "running") return { state: "working", paused: false };
  if (state === "failed" || state === "stopped") return { state: "default", paused: true };
  return { state: "default", paused: false };
}

/** One helper's bot, like a profile picture. `bot` is the server's face index; without it, the id's hash. */
export function HelperBot({ bot, id, state, size }: { bot?: number; id?: string; state: HelperState; size: number }) {
  const face = helperFace(bot, id);
  const mood = botState(state);
  return (
    <span className="helperBot" style={{ width: size, height: size }} aria-hidden="true">
      <Suspense fallback={null}>
        <BotAvatar type={face.type} color={face.color} size={size} state={mood.state} paused={mood.paused}
          interactive={false} theme="dark" />
      </Suspense>
    </span>
  );
}

/** The thinking orb ("solving", the 20 px inline design) beside words. */
export function HelperOrb() {
  return (
    <span className="helperOrb" aria-hidden="true">
      <Suspense fallback={<span className="helperOrb__slot" />}>
        <ThinkingOrb state="solving" size={20} theme="dark" />
      </Suspense>
    </span>
  );
}
